import React, { useEffect, useRef } from "react";
import "./AboutUs.css";
import aboutOffice from "../assets/about-office.png";
import technologyImpact from "../assets/technology-impacts.png";

import {
  FaArrowRight,
  FaBullseye,
  FaEye,
  FaLightbulb,
  FaUsers,
  FaShieldAlt,
  FaChartLine,
  FaCode,
  FaBrain,
  FaFlask,
  FaCogs,
  FaSeedling,
} from "react-icons/fa";

const AboutUs = () => {
  const aboutSectionRef = useRef(null);

  useEffect(() => {
    const section = aboutSectionRef.current;

    if (!section) return;

    const elements = section.querySelectorAll(".animate-on-scroll");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("about-visible");
          }
        });
      },
      {
        threshold: 0.15,
      },
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page" ref={aboutSectionRef}>
      {/* =====================================================
          ABOUT HERO SECTION
      ===================================================== */}
      <section className="about-hero">
        <div className="about-hero-overlay"></div>

        <div className="about-hero-content animate-on-scroll">
          {/* BREADCRUMB */}
          <div className="about-breadcrumb">
            <span>Home</span>
            <span className="breadcrumb-arrow">›</span>
            <span className="active">About Us</span>
          </div>

          <div className="section-label hero-label">
            <span></span>
            ABOUT US
          </div>

          <h1>
            Building Technology
            <br />
            That <span>Moves Business Forward</span>
          </h1>

          <p>
            At MindstreamX, we turn ideas into innovative digital solutions that
            help businesses work smarter, grow faster, and create a better
            tomorrow.
          </p>

          <button
            className="primary-btn"
            onClick={() =>
              document
                .getElementById("about")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Our Journey
            <FaArrowRight />
          </button>
        </div>

        <div className="hero-side-text">
          <span>Ideas</span>
          <span>Technology</span>
          <span>Impact</span>
        </div>

        <div className="hero-bottom-text">
          <span>People</span>
          <i></i>
          <span>Innovation</span>
          <i></i>
          <span>Growth</span>
          <i></i>
          <span>A Smarter Tomorrow</span>
        </div>
      </section>

      {/* =====================================================
          WHO WE ARE
      ===================================================== */}
      <section className="who-we-are" id="about">
        <div className="container who-we-are-container">
          {/* LEFT CONTENT */}
          <div className="who-content animate-on-scroll">
            <div className="section-label">
              <span></span>
              WHO WE ARE
            </div>

            <h2>
              We are <span>MindstreamX</span>
            </h2>

            <h3>A technology-focused company with a people-first mindset.</h3>

            <p>
              MindstreamX Technologies is a technology-driven company powered by
              a team of 100+ professionals, delivering innovative software
              products and digital solutions. We combine technical expertise,
              creativity, and strategic thinking to help businesses overcome
              challenges, optimize operations, and accelerate growth.
            </p>

            <p>
              From enterprise solutions to next-generation digital platforms, we
              build secure, scalable, and future-ready technologies that create
              meaningful business value. Our commitment to quality, innovation,
              and long-term partnerships empowers businesses to thrive in an
              ever-evolving digital landscape.
            </p>

            {/* FEATURES */}
            <div className="who-features">
              <div className="who-feature animate-on-scroll">
                <span className="check-icon">✓</span>
                <span>Business-focused solutions</span>
              </div>

              <div className="who-feature animate-on-scroll">
                <span className="check-icon">✓</span>
                <span>Modern technology</span>
              </div>

              <div className="who-feature animate-on-scroll">
                <span className="check-icon">✓</span>
                <span>Scalable architecture</span>
              </div>

              <div className="who-feature animate-on-scroll">
                <span className="check-icon">✓</span>
                <span>Long-term partnership</span>
              </div>
            </div>

            <button
              className="primary-btn"
              onClick={() =>
                document
                  .getElementById("what-we-do")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              More About Us
              <span className="arrow">→</span>
            </button>
          </div>

          {/* RIGHT IMAGE */}
          <div className="who-image-wrapper animate-on-scroll">
            <img
              src={aboutOffice}
              alt="MindstreamX Office"
              className="who-image"
            />

            {/* FLOATING CARD */}
            <div className="floating-about-card">
              <span>Innovate</span>
              <span>Build</span>
              <span>Grow</span>
              <span>Together</span>

              <div className="floating-line"></div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          WHAT WE DO
      ===================================================== */}
      <section className="what-we-do" id="what-we-do">
        <div className="container">
          <div className="section-heading-row animate-on-scroll">
            <div>
              <div className="section-label">
                <span></span>
                WHAT WE DO
              </div>

              <h2>
                What We <span>Do Best</span>
              </h2>
            </div>

            <p>
              Technology solutions designed to solve real business challenges.
            </p>
          </div>

          <div className="services-grids">
            {/* Card 1 */}
            <div className="about-service-card animate-on-scroll">
              <div className="service-icons blue-icon">
                <FaCode />
              </div>

              <h3>Software Development</h3>

              <p>
                Custom software solutions built to turn your ideas into powerful
                digital products.
              </p>
            </div>

            {/* Card 2 */}
            <div className="about-service-card animate-on-scroll">
              <div className="service-icon purple-icon">
                <FaBrain />
              </div>

              <h3>AI & Emerging Technologies</h3>

              <p>
                Exploring AI, automation and next-gen technologies to create
                smarter solutions.
              </p>
            </div>

            {/* Card 3 */}
            <div className="about-service-card animate-on-scroll">
              <div className="service-icon cyan-icon">
                <FaFlask />
              </div>

              <h3>Research & Development</h3>

              <p>
                Innovating and experimenting to build scalable, future-ready
                solutions.
              </p>
            </div>

            {/* Card 4 */}
            <div className="about-service-card animate-on-scroll">
              <div className="service-icon dark-blue-icon">
                <FaCogs />
              </div>

              <h3>Business & Enterprise Solutions</h3>

              <p>
                Helping businesses streamline operations and achieve greater
                efficiency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          MISSION & VISION
      ===================================================== */}
      <section className="mission-vision">
        <div className="mission-overlay"></div>

        <div className="container mission-container">
          <div className="section-label light-label animate-on-scroll">
            <span></span>
            OUR MISSION & VISION
          </div>

          <h2 className="animate-on-scroll">
            Driven by Purpose, Focused on a <span>Smarter Tomorrow</span>
          </h2>

          <div className="mission-grid">
            {/* Mission */}
            <div className="mission-box animate-on-scroll">
              <div className="mission-icon">
                <FaBullseye />
              </div>

              <div>
                <h3>Our Mission</h3>

                <p>
                  To create reliable and innovative technology solutions that
                  solve real business challenges.
                </p>
              </div>
            </div>

            <div className="mission-divider"></div>

            {/* Vision */}
            <div className="mission-box animate-on-scroll">
              <div className="mission-icon">
                <FaEye />
              </div>

              <div>
                <h3>Our Vision</h3>

                <p>
                  To be a trusted technology company known for building
                  meaningful products and delivering lasting value.
                </p>
              </div>
            </div>
          </div>

          <div className="mission-side-text">
            <span>Higher</span>
            <span>Together</span>
          </div>
        </div>
      </section>

      {/* =====================================================
          OUR APPROACH
      ===================================================== */}
      <section className="our-approach">
        <div className="container">
          <div className="section-heading-row animate-on-scroll">
            <div>
              <div className="section-label">
                <span></span>
                HOW WE WORK
              </div>

              <h2>
                Our <span>Approach</span>
              </h2>
            </div>

            <p>A simple yet powerful approach to turn ideas into impact.</p>
          </div>

          <div className="approach-grid">
            {/* Innovation */}
            <div className="approach-item animate-on-scroll">
              <div className="approach-icon">
                <FaLightbulb />
              </div>

              <h3>Innovation</h3>

              <p>We continuously explore better technologies and ideas.</p>
            </div>

            {/* Customer Focus */}
            <div className="approach-item animate-on-scroll">
              <div className="approach-icon">
                <FaUsers />
              </div>

              <h3>Customer Focus</h3>

              <p>We understand the problem before building the solution.</p>
            </div>

            {/* Quality */}
            <div className="approach-item animate-on-scroll">
              <div className="approach-icon">
                <FaShieldAlt />
              </div>

              <h3>Quality</h3>

              <p>We focus on reliable, scalable and maintainable solutions.</p>
            </div>

            {/* Continuous Growth */}
            <div className="approach-item animate-on-scroll">
              <div className="approach-icon">
                <FaChartLine />
              </div>

              <h3>Continuous Growth</h3>

              <p>We learn, improve and evolve with technology.</p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          TECHNOLOGY WITH PURPOSE
      ===================================================== */}
      <section className="technology-purpose">
        <div className="technology-image animate-on-scroll">
          <img src={technologyImpact} alt="Technology" />
        </div>

        <div className="technology-content animate-on-scroll">
          <div className="section-label light-label">
            <span></span>
            TECHNOLOGY WITH PURPOSE
          </div>

          <h2>
            Technology That <span>Creates Impact</span>
          </h2>

          <p>
            We don't build technology just for the sake of technology. We focus
            on creating solutions that have a clear purpose and practical value
            — solutions that make a real difference for our clients and the
            communities they serve.
          </p>
        </div>

        <div className="technology-points">
          {/* Real Solutions */}
          <div className="technology-point animate-on-scroll">
            <div className="technology-icons">
              <FaCogs />
            </div>

            <div className="technology-point-content">
              <h3>Real Solutions</h3>

              <p>Practical technology designed around real business needs.</p>
            </div>
          </div>

          {/* Stronger Businesses */}
          <div className="technology-point animate-on-scroll">
            <div className="technology-icons">
              <FaChartLine />
            </div>

            <div className="technology-point-content">
              <h3>Stronger Businesses</h3>

              <p>Solutions that help businesses improve and grow.</p>
            </div>
          </div>

          {/* Brighter Tomorrow */}
          <div className="technology-point animate-on-scroll">
            <div className="technology-icons">
              <FaSeedling />
            </div>

            <div className="technology-point-content">
              <h3>Brighter Tomorrow</h3>

              <p>Technology designed with long-term impact in mind.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
